/**
 * مشکل‌های دیده‌شده روی یه رخداد — منطقِ فهرست، بدونِ هیچ رفت‌وبرگشتی به سرور.
 *
 * جدولش یه سطر برای هر «این مشکل، روی این رخداد» داره (مایگریشن 0082) و
 * همین سه تابع کلِ چیزی‌ان که دو تا کلاینت ازش می‌سازن:
 *
 *   ۱. **فهرستِ مشکل‌ها** = متن‌های یکتا در همه‌ی سطرهای من، تازه‌ترین اول.
 *      جای جدا برای نگه داشتنِ این فهرست نیست: مشکلِ تازه که نوشته شد، خودش
 *      از دفعه‌ی بعد توی فهرست هست.
 *   ۲. **علامتِ کنارِ هر سطر** = آیا همین متن روی همین رخداد هم هست.
 *   ۳. **یکدست کردنِ متن** قبل از فرستادن، تا «عکس نداره» و «عکس  نداره »
 *      دو تا مشکلِ جدا نشن (قیدِ یکتاییِ دیتابیس متن رو کاراکتربه‌کاراکتر
 *      مقایسه می‌کنه و از خودش چیزی کم نمی‌کنه).
 *   ۴. **مقدارِ درست**، کنارِ خودِ اشاره: «عنوانش غلطه» به‌تنهایی فقط
 *      می‌گه کجا رو نگاه کن؛ `title=عنوانِ درست` می‌گه چی بذار. همون یه
 *      ستونِ `text` نگهش می‌داره (`makeFieldDetail`) تا نه مایگریشنی لازم
 *      شه نه سطرهای بی‌مقدارِ قبلی معنی‌شون عوض شه.
 *
 * چرا این‌جا و نه توی صفحه: دو تا صفحه هست (گوشی و سایت) و هر دو عیناً همین
 * فهرست رو نشون می‌دن — و این‌طوری بدونِ رندر کردنِ چیزی تست می‌شه.
 */

/** یه سطرِ `item_issues`. */
export interface ItemIssue {
  id: number;
  eventId: string;
  text: string;
  /** ISO. تنها کاری که باهاش می‌کنیم مرتب کردنِ فهرسته. */
  createdAt: string;
}

/** همون سقفی که مایگریشن 0082 هم می‌گیره. */
export const ISSUE_MAX = 300;

export type IssueKind = 'missed' | 'wrong' | 'inappropriate' | 'duplicate' | 'other';
const STRUCTURED_PREFIX = 'goout-issue:';

/** یک ردیفِ ریشه‌ی منو: دسته، و کلیدِ i18nِ برچسبش. */
export interface IssueKindDef {
  kind: IssueKind;
  labelKey: string;
}

/**
 * دسته‌های ریشه‌ی منو، به ترتیب — **بی** «چیزِ دیگری».
 *
 * ═══ چرا «چیزِ دیگری» این‌جا نیست ═══
 *
 * ریشه‌ی منو دو چیز پشتِ سرِ هم نشان می‌دهد: این دسته‌ها، و بعد میان‌برِ
 * متن‌هایی که خودت قبلاً زیرِ «چیزِ دیگری» نوشته‌ای. تا وقتی «چیزِ دیگری»
 * تهِ همین آرایه بود، آن میان‌برها **بعد** از آن می‌آمدند و «چیزِ دیگری»
 * وسطِ فهرست می‌افتاد — درحالی‌که کلِ معنی‌اش «هیچ‌کدامِ بالایی‌ها» است و
 * گزینه‌ای با این معنی باید آخر باشد، وگرنه خواننده مجبور است بعد از
 * دیدنش باز هم پایین را بخواند.
 *
 * پس جدا شد تا هر دو رابط ناچار باشند بینشان جا بگذارند:
 * `ISSUE_KINDS` → میان‌برهای ذخیره‌شده → `ISSUE_KIND_OTHER`.
 */
export const ISSUE_KINDS: IssueKindDef[] = [
  { kind: 'missed', labelKey: 'issueMissed' },
  { kind: 'wrong', labelKey: 'issueWrong' },
  { kind: 'inappropriate', labelKey: 'issueInappropriate' },
  { kind: 'duplicate', labelKey: 'issueDuplicate' },
];

/** همیشه آخرین گزینه‌ی ریشه — نگاه کن به `ISSUE_KINDS`. */
export const ISSUE_KIND_OTHER: IssueKindDef = { kind: 'other', labelKey: 'issueOther' };

export interface ParsedIssue {
  kind: IssueKind;
  detail: string;
}

/** Stable storage format; labels can be translated without changing saved selections. */
export function makeIssueText(kind: IssueKind, detail: string): string {
  return normalizeIssue(`${STRUCTURED_PREFIX}${kind}:${detail}`);
}

/** Rows created by the old parvij-only free-text menu remain available under Other. */
export function parseIssueText(text: string): ParsedIssue {
  if (!text.startsWith(STRUCTURED_PREFIX)) return { kind: 'other', detail: text };
  const rest = text.slice(STRUCTURED_PREFIX.length);
  const split = rest.indexOf(':');
  if (split < 0) return { kind: 'other', detail: text };
  const kind = rest.slice(0, split) as IssueKind;
  const detail = rest.slice(split + 1);
  if (!(['missed', 'wrong', 'inappropriate', 'duplicate', 'other'] as string[]).includes(kind)) {
    return { kind: 'other', detail: text };
  }
  return { kind, detail };
}

/**
 * جداکننده‌ی «مقدارِ درست» داخلِ detail: `title=عنوانِ درست`.
 *
 * چرا `=` و نه همان `:`ی که `tag:…` دارد: آن یکی می‌گوید «این تگ اشتباه
 * است» و این یکی «درستش این است». دو معنیِ متفاوت‌اند و با یک جداکننده،
 * `tag:جاز` و «تگِ درست جاز است» یک سطرِ یکسان می‌شدند.
 */
const FIELD_FIX_SEP = '=';

/** یک detailِ فیلددار، باز شده. */
export interface FieldIssue {
  /** همان `value`ی ردیفِ فهرستِ فیلدها: `title`، `image`، `when`، … */
  field: string;
  /** مقدارِ درستی که کاربر داده — یا `null` وقتی فقط علامت زده. */
  fix: string | null;
}

/**
 * detailِ «این فیلد، و اگر گفت، درستش».
 *
 * مقدارِ خالی یعنی همان سطرِ قدیمی (فقط نامِ فیلد) — پس علامت‌زدنِ ساده هنوز
 * دقیقاً همان متنِ قبلی را می‌سازد و سطرهای ثبت‌شده‌ی قبلی بی‌معنی نمی‌شوند.
 */
export function makeFieldDetail(field: string, fix?: string | null): string {
  const clean = normalizeIssue(fix ?? '');
  return clean ? `${field}${FIELD_FIX_SEP}${clean}` : field;
}

/** وارونِ `makeFieldDetail`. سطرهای قدیمیِ بی‌مقدار `fix: null` می‌دهند. */
export function parseFieldDetail(detail: string): FieldIssue {
  const at = detail.indexOf(FIELD_FIX_SEP);
  if (at <= 0) return { field: detail, fix: null };
  return { field: detail.slice(0, at), fix: detail.slice(at + 1) || null };
}

/**
 * چند کاراکتر برای خودِ مقدار می‌ماند.
 *
 * قیدِ ۳۰۰ کاراکتریِ دیتابیس روی **کلِ** متن است، یعنی برچسب‌ها هم از همان
 * سهم می‌خورند. فرم با این عدد جلوی تایپِ اضافه را می‌گیرد؛ وگرنه
 * `normalizeIssue` بی‌صدا ته حرفِ آدم را می‌برید.
 */
export const fieldFixRoom = (kind: IssueKind, field: string): number =>
  Math.max(0, ISSUE_MAX - `${STRUCTURED_PREFIX}${kind}:${field}${FIELD_FIX_SEP}`.length);

/** یک ردیفِ فهرستِ فیلدها: مقدارِ ذخیره‌شونده، و کلیدِ i18nِ برچسبش. */
export interface IssueFieldDef {
  /**
   * چیزی که در `text` می‌نشیند. **هرگز عوض نمی‌شود** — سطرهای ثبت‌شده‌ی
   * قبلی همین رشته را دارند و عوض کردنش آن‌ها را بی‌صاحب می‌کند. برچسب
   * ولی هر وقت خواستیم عوض می‌شود، چون هیچ‌جا ذخیره نمی‌شود.
   */
  value: string;
  /** کلیدِ i18n — خودِ ترجمه در رابط انجام می‌شود تا این فایل خالص بماند. */
  labelKey: string;
}

/**
 * فهرستِ فیلدهایی که می‌شود «جا افتاده ..» یا «غلط است ..» رویشان زد.
 *
 * ═══ چرا این‌جا، و چرا بی‌شرط ═══
 *
 * قبلاً هر صفحه فهرستِ خودش را می‌ساخت و بدتر: **شرطی**. صفحه‌ی کشف فقط
 * وقتی «چند» را نشان می‌داد که رخداد `isFree` بود، و صفحه‌ی جزئیات فهرست را
 * از `itemFields()` می‌گرفت که ردیفِ بی‌مقدار را اصلاً نمی‌سازد. یعنی دقیقاً
 * همان رخدادی که قیمتش جا افتاده بود، تنها رخدادی بود که نمی‌شد «قیمتش جا
 * افتاده» را رویش ثبت کرد (WHOSUP-226). همین برای دکمه‌ی خریدِ بلیط هم
 * می‌افتاد، که اصلاً در هیچ فهرستی نبود.
 *
 * پس فهرست یکی است، در هسته، و همیشه کامل: نبودنِ یک مقدار خودش همان چیزی
 * است که می‌خواهیم گزارش شود.
 *
 * `tag` عمداً آخر است و در هر دو رابط رفتارِ ویژه دارد (خودش یک فهرستِ
 * جداست، نه یک کادرِ متن).
 */
export const ISSUE_FIELDS: IssueFieldDef[] = [
  { value: 'title', labelKey: 'issueFieldTitle' },
  { value: 'image', labelKey: 'issueFieldImage' },
  { value: 'description', labelKey: 'issueFieldDescription' },
  /* قالبِ توضیحات جدا از خودِ توضیحات: متنی که درست است ولی یک‌تکه‌ی
     بی‌پاراگراف یا پر از خطِ خالی و ته‌ماندهٔ HTML است، مشکلِ «توضیحات غلط
     است» نیست — مشکلِ شکلش است، و تا وقتی اسمِ خودش را نداشت زیرِ
     «توضیحات» گم می‌شد. */
  { value: 'description_format', labelKey: 'issueFieldDescFormat' },
  /* «کِی»/«کجا»/«چند» در صفحه‌ی جزئیات سرِ جایشان‌اند و آن‌جا عمداً سؤال‌اند؛
     این‌جا اسمِ خودِ چیز بهتر است، چون این فهرست سؤال نمی‌پرسد — فیلد را
     نام می‌برد. پس کلیدِ جدا، نه عوض کردنِ `fieldWhen`/`fieldWhere`/
     `fieldPrice`ی مشترک. */
  { value: 'when', labelKey: 'issueFieldTime' },
  { value: 'where', labelKey: 'issueFieldAddress' },
  { value: 'price', labelKey: 'issueFieldPrice' },
  /* دکمه‌ی «خریدِ بلیط» (`events.ticket_url`، مایگریشن 0077). نبودنش روی
     رخدادی که بلیط می‌فروشد، و بودنش با لینکی که به صفحه‌ی خرید نمی‌رسد،
     هر دو مشکلِ واقعی‌اند و تا حالا جایی برای ثبت نداشتند. */
  { value: 'ticket', labelKey: 'issueFieldTicket' },
  /* لینکِ منبع — همان چیزی که دکمه‌ی «باز کن ›» باز می‌کند. غلط بودنش از
     همه بدتر است: آدرسی که به صفحه‌ی رخداد نمی‌رسد یعنی خواننده هیچ راهی
     برای رسیدن به خودِ رخداد ندارد. */
  { value: 'link', labelKey: 'issueFieldLink' },
  { value: 'tag', labelKey: 'issueFieldTags' },
];

/** فیلدهایی که روی همین رخداد، در همین دسته، چیزی رویشان ثبت شده. */
export function issueFieldsOn(rows: ItemIssue[], eventId: string, kind: IssueKind): Set<string> {
  return new Set(rows.filter((row) => row.eventId === eventId)
    .map((row) => parseIssueText(row.text))
    .filter((parsed) => parsed.kind === kind)
    .map((parsed) => parseFieldDetail(parsed.detail).field));
}

/**
 * هرچه روی همین فیلد ثبت شده، تازه‌ترین اول.
 *
 * `detail` برمی‌گردد و نه فقط `fix`، چون تنها راهِ پس گرفتنِ یک سطر همان
 * متنِ دقیقش است.
 */
export function fieldIssuesOn(
  rows: ItemIssue[], eventId: string, kind: IssueKind, field: string,
): { detail: string; fix: string | null }[] {
  return [...rows].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .filter((row) => row.eventId === eventId)
    .map((row) => parseIssueText(row.text))
    .filter((parsed) => parsed.kind === kind)
    .map((parsed) => ({ detail: parsed.detail, ...parseFieldDetail(parsed.detail) }))
    .filter((parsed) => parsed.field === field)
    .map(({ detail, fix }) => ({ detail, fix }));
}

/**
 * چه چیزی باید نوشته و چه چیزی برداشته شود — خروجیِ هر دو تابعِ پایین.
 *
 * `add`/`remove` و نه «انجامش بده»، چون این فایل به دیتابیس دست نمی‌زند؛ هر
 * دو رابط همین نقشه را می‌گیرند و با `issueRepo` اجرایش می‌کنند.
 */
export interface IssuePlan {
  add: string | null;
  remove: string[];
}

/**
 * زدنِ نامِ فیلد **همان‌جا** ثبتش می‌کند، بی‌هیچ دکمه‌ای.
 *
 * ═══ چرا ═══
 *
 * نسخه‌ی اولِ قدمِ «مقدارِ درست» تا «ذخیره» زده نمی‌شد هیچ چیزی ثبت نمی‌کرد،
 * و همین آن کادر را اجباری جلوه می‌داد: کسی که فقط می‌خواست بگوید «عنوانش
 * غلط است» و درستش را نمی‌دانست، یا بی‌خود چیزی می‌نوشت یا نصفه‌راه رها
 * می‌کرد و اصلاً چیزی ثبت نمی‌شد. حالا علامت با همان یک لمسِ قبلی می‌نشیند
 * و مقدارِ درست یک پیشنهادِ اختیاریِ بعدش است.
 *
 * فیلدی که از قبل چیزی رویش هست دوباره ثبت نمی‌شود — نه سطرِ تکراری بسازد
 * (که قیدِ یکتاییِ دیتابیس هم نمی‌گذارد) و نه سطرِ مقداردارِ موجود را با یک
 * سطرِ بی‌مقدارِ تازه شلوغ کند.
 */
export function markFieldPlan(
  rows: ItemIssue[], eventId: string, kind: IssueKind, field: string,
): IssuePlan {
  if (fieldIssuesOn(rows, eventId, kind, field).length) return { add: null, remove: [] };
  return { add: makeIssueText(kind, field), remove: [] };
}

/**
 * افزودنِ مقدارِ درست به فیلدی که همین حالا علامت خورده.
 *
 * سطرِ «فقط علامت» برداشته می‌شود: وقتی نوشته‌ای «عنوانِ درست فلان است»،
 * سطرِ «عنوانش غلط است» دیگر حرفِ تازه‌ای ندارد و دو سطر برای یک حرف فقط
 * فهرست را دوتا می‌کند. مقدارهای دیگر سرِ جایشان می‌مانند — دو منبعِ متفاوت
 * می‌توانند دو عنوانِ درست پیشنهاد دهند.
 */
export function fixFieldPlan(
  rows: ItemIssue[], eventId: string, kind: IssueKind, field: string, fix: string,
): IssuePlan {
  const clean = normalizeIssue(fix);
  if (!clean) return { add: null, remove: [] };
  const text = makeIssueText(kind, makeFieldDetail(field, clean));
  const on = fieldIssuesOn(rows, eventId, kind, field);
  return {
    add: on.some((row) => makeIssueText(kind, row.detail) === text) ? null : text,
    remove: on.filter((row) => row.fix === null).map((row) => makeIssueText(kind, row.detail)),
  };
}

export function issueDetailCatalog(rows: ItemIssue[], kind: IssueKind): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of [...rows].sort((a, b) => b.createdAt.localeCompare(a.createdAt))) {
    const parsed = parseIssueText(row.text);
    if (parsed.kind !== kind || seen.has(parsed.detail)) continue;
    seen.add(parsed.detail);
    out.push(parsed.detail);
  }
  return out;
}

export const otherIssueCatalog = (rows: ItemIssue[]): string[] => issueDetailCatalog(rows, 'other');

/** Search used by both tag pickers; preserves catalog order and ignores case/spacing. */
export function filterIssueTags(tags: string[], query: string): string[] {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return tags;
  return tags.filter((tag) => tag.toLocaleLowerCase().includes(needle));
}

/**
 * متن، همون‌طور که ذخیره می‌شه: فاصله‌های دو طرف رفته، هر رشته فاصله‌ی وسط
 * یه فاصله شده، و بریده به سقف.
 *
 * خطِ تازه هم فاصله حساب می‌شه: این یه برچسبِ یه‌خطیه که قراره توی فهرست
 * دوباره پیدا شه، و متنِ چندخطی توی اون فهرست فقط ردیف‌ها رو به هم می‌ریزه.
 */
export function normalizeIssue(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim().slice(0, ISSUE_MAX);
}

/** فهرستِ متن‌های یکتا، تازه‌ترین اول. */
export function issueCatalog(rows: ItemIssue[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of [...rows].sort((a, b) => b.createdAt.localeCompare(a.createdAt))) {
    if (seen.has(r.text)) continue;
    seen.add(r.text);
    out.push(r.text);
  }
  return out;
}

/** متن‌هایی که روی همین رخداد ثبت شده‌ان. */
export function issuesOn(rows: ItemIssue[], eventId: string): Set<string> {
  return new Set(rows.filter((r) => r.eventId === eventId).map((r) => r.text));
}

/** Categories that already contain at least one report for this event. */
export function issueKindsOn(rows: ItemIssue[], eventId: string): Set<IssueKind> {
  return new Set(rows.filter((row) => row.eventId === eventId)
    .map((row) => parseIssueText(row.text).kind));
}

/**
 * خودِ فهرستی که دکمه باز می‌کنه: همه‌ی مشکل‌های تا حالا، با علامتِ اینکه
 * کدومشون روی همین رخداد هست.
 *
 * ترتیب عمداً همیشه یکیه (تازه‌ترین اول) و علامت‌خورده‌ها بالا نمی‌آن: با
 * فهرستی که جای سطرهاش از رخدادی به رخدادِ دیگه عوض شه، هیچ‌وقت نمی‌شه بدونِ
 * خوندن زد.
 */
export function issueMenu(
  rows: ItemIssue[], eventId: string,
): { text: string; on: boolean }[] {
  const on = issuesOn(rows, eventId);
  return issueCatalog(rows).map((text) => ({ text, on: on.has(text) }));
}
